import { createContext, useContext, useState, useCallback, useRef, ReactNode, useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { SyncService } from '@/service';
import { AppSettingsDB } from '@/db/repositories';
import { useToast } from '@/context/ToastContext';
import { onlineStatus } from '@/utils';

type SyncStatus = 'syncing' | 'done' | 'error';

/* eslint-disable no-unused-vars */
interface BackgroundSyncContextValue {
  syncStatus: SyncStatus;
  isSyncComplete: boolean;
  syncError: Error | null;
  startSync: (options?: { forceUpdate?: boolean }) => void;
  lastSyncDate: Date | null;
}
/* eslint-enable no-unused-vars */

const BackgroundSyncContext = createContext<BackgroundSyncContextValue | undefined>(undefined);

export const useBackgroundSync = () => {
  const context = useContext(BackgroundSyncContext);
  if (!context) throw new Error('useBackgroundSync must be used within BackgroundSyncProvider');
  return context;
};

interface BackgroundSyncProviderProps {
  children?: ReactNode;
}

export const BackgroundSyncProvider = ({ children }: BackgroundSyncProviderProps) => {
  const { showToast } = useToast();
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('syncing');
  const [syncError, setSyncError] = useState<Error | null>(null);
  const [lastSyncDate, setLastSyncDate] = useState<Date | null>(null);
  const isSyncingRef = useRef(false);
  const initialSyncRef = useRef(false);

  useEffect(() => {
    const loadLastSync = async () => {
      const record = await AppSettingsDB.get('lastSyncDate');
      if (record?.value) {
        setLastSyncDate(new Date(record.value as string));
      }
    };
    loadLastSync();
  }, []);

  const startSync = useCallback(
    async (options?: { forceUpdate?: boolean }) => {
      // Prevent concurrent sync runs
      if (isSyncingRef.current) return;

      // If offline, don't even try to sync
      if (!onlineStatus.get()) return;

      isSyncingRef.current = true;
      setSyncStatus('syncing');
      setSyncError(null);

      try {
        await SyncService.runAllSyncs(options);
        const now = new Date();
        setSyncStatus('done');
        setLastSyncDate(now);
        await AppSettingsDB.set('lastSyncDate', now.toISOString());
      } catch (error) {
        console.error('Background sync failed:', error);
        const syncErr = error instanceof Error ? error : new Error('Background sync failed');
        setSyncError(syncErr);
        setSyncStatus('error');
        showToast({
          message: 'Failed to sync data in the background. Some features may be unavailable.',
          status: 'error',
          duration: 5000,
        });
      } finally {
        isSyncingRef.current = false;
      }
    },
    [showToast],
  );

  useEffect(() => {
    // Initial sync trigger on mount exactly once
    if (!initialSyncRef.current) {
      initialSyncRef.current = true;
      startSync();
    }
  }, [startSync]);

  return (
    <BackgroundSyncContext.Provider
      value={{
        syncStatus,
        isSyncComplete: syncStatus === 'done',
        syncError,
        startSync,
        lastSyncDate,
      }}
    >
      {children ?? <Outlet />}
    </BackgroundSyncContext.Provider>
  );
};

export default function BackgroundSyncProviderWrapper() {
  return (
    <BackgroundSyncProvider>
      <Outlet />
    </BackgroundSyncProvider>
  );
}
